import useSWRImmutable from "swr/immutable"

import { useGetUserData } from "../../../hooks/useGetUserData"
import { canAccessProtectedRoutes } from "../../../utils/auth/authUtils"
import { getClientProtected, getClientPublic } from "../clients/clients"
import {
    TGenRefOutput,
    genFilterReferenceMapper,
} from "../mappers/genFilterReferenceMapper"
import { GEN_REFERENCE } from "../urls/hybrid"

type TMultiFetcherUseFilterReference = {
    referenceData: TGenRefOutput | undefined
    isLoading: boolean
    isError: boolean
}

/**
 * Hook for getting TTR filter panel reference data. Reference data is
 * immutable (it is highly unlikely to change over the course of a session)
 * and therefore the `useSWRImmutable` hook is used.
 *
 * This hook has a conditional fetcher, which routes requests through
 * either the public or protected axios client based on auth status and role.
 * The request is deferred if the auth status is `isConfiguring`.
 * Therefore, the `isLoading` output value is augmented with this
 * parameter for this hook.
 *
 * @returns {TMultiFetcherUseFilterReference} - SWR data, loading and error states.
 */
const useMultiFetcherGenFilterReferenceData =
    (): TMultiFetcherUseFilterReference => {
        const { isSignedIn, isConfiguring, groups } = useGetUserData()
        const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)

        const {
            data: referenceData,
            error,
            isLoading,
        } = useSWRImmutable(
            isConfiguring ? null : GEN_REFERENCE(canAccessProtected),
            async (url: string): Promise<TGenRefOutput> => {
                try {
                    const fetcher = canAccessProtected
                        ? getClientProtected
                        : getClientPublic
                    const res = await fetcher(url)
                    return genFilterReferenceMapper.map(res)
                } catch {
                    throw Error()
                }
            }
        )

        return {
            referenceData,
            isLoading: isLoading || isConfiguring, // See notes in docstring
            isError: error,
        }
    }

export default useMultiFetcherGenFilterReferenceData
